import React, { useEffect, useState } from 'react'
import RpaClaimSubmitButton from '../../../../claimForm/submitClaim/RpaClaimSubmitButton'
import { connect, useDispatch } from 'react-redux'
import EyefinityStepForm from './EyefinityStepForm'
import { reduxForm } from 'redux-form'
import ScrollToTarget from '../../../../../../../../../itrust_common/components/others/ScrollToTarget'
import ClaimFormOverview from '../ClaimFormOverview'
import ClaimFormSync from '../../../../../../../../../components/authorised/order/edit/ClaimFormSync'
import Alert from '../../../../../../../../../itrust_common/components/Alert'
import { visionInsuranceClaimFormEdit, visionInsuranceClaimFormUpdate } from '../../../../../../../../../redux/actions/addOns/rpa/vision_insurance/visionInsuranceClaimFormAction'
import { visionInsuranceServicesLoad } from '../../../../../../../../../redux/actions/addOns/rpa/vision_insurance/visionInsuranceAction'
import DateFormatView from '../../../../../../../../../itrust_common/components/others/DateFormatView'
import { required } from '../../../../../../../../../itrust_common/components/form/FormValidations'
import { shouldShowClaimSection } from './eyefinityServiceSections'
import { shouldDisableEyefinityField } from './eyefinityFieldDisableRules'
import { DIRECT_SAVE_CLAIM_OPTIONS, isDirectSaveClaimOrg } from '../../../../claimForm/directSaveClaimOrgHelper'
import { withOrdersInsuranceId } from '../../../../../../../../../components/authorised/order/claimFormDropdownUtils'

const PAGE1_SECTIONS = [
  'basic_info',
  'exams',
  'lens',
  'frame',
  'lab',
  'contact_lens',
  'contact_lenses_service',
  'prescription',
]

const getValueByPath = (values, path) => (
  path?.split('.').reduce((acc, key) => acc?.[key], values)
)

const shouldShowField = (field, sectionName, formValues) => {
  if (!field.show_when) return true

  const showWhenValue = getValueByPath(formValues?.[sectionName], field.show_when.field)
    ?? getValueByPath(formValues, field.show_when.field)

  if (field.show_when.value_in) {
    const valueIn = Array.isArray(field.show_when.value_in)
      ? field.show_when.value_in
      : [field.show_when.value_in]

    return valueIn.includes(showWhenValue)
  }

  return showWhenValue === field.show_when.value
}

const setFieldError = (errors, sectionName, fieldLabel, message) => {
  if (!errors[sectionName]) errors[sectionName] = {}
  errors[sectionName][fieldLabel] = message
}

const validateTemplateFields = (values, config, sections) => {
  const errors = {}

  sections.forEach((sectionName) => {
    if (!shouldShowClaimSection(sectionName, values?.eligibility?.data)) return

    ;(config[sectionName] || []).forEach((field) => {
      const validateField = (fieldConfig) => {
        if (fieldConfig.type === 'checkbox') return
        if (!shouldShowField(fieldConfig, sectionName, values)) return
        if (shouldDisableEyefinityField({ fieldLabel: fieldConfig.label, sectionName, formValues: values })) return

        const value = values?.[sectionName]?.[fieldConfig.label]
        if (required(value)) {
          setFieldError(errors, sectionName, fieldConfig.label, 'Required')
        }
      }

      if (field.type === 'section') {
        field.options?.forEach(validateField)
        return
      }

      validateField(field)
    })
  })

  return errors
}

const validateEyefinityClaimForm = (values, props) => {
  const { editing: { activeTab } = {}, claimFormTemplate } = props
  const config = claimFormTemplate?.config || {}

  if (activeTab === 2) {
    return validateTemplateFields(values, config, PAGE1_SECTIONS)
  }

  return {}
}

function EyefinityClaimForm(props) {
  const { visionInsuranceConfig, order, handleSubmit, editing, claimFormTemplate, visionInsuranceClaimForm, services, organisation } = props

  const dispatch = useDispatch()
  const isDirectSaveOrg = isDirectSaveClaimOrg(organisation?.subdomain, order?.store_id)
  const firstStepActionLabel = isDirectSaveOrg ? 'Save' : 'Next'
  const [selectedLocation, setSelectedLocation] = useState(
    visionInsuranceClaimForm?.location || DIRECT_SAVE_CLAIM_OPTIONS[0].value
  )
  let filters = {}

  useEffect(() => {
    if (visionInsuranceClaimForm?.location) {
      setSelectedLocation(visionInsuranceClaimForm.location)
    }
  }, [visionInsuranceClaimForm?.location])

  useEffect(() => {
    if (!services?.length && order?.store_id) {
      dispatch(visionInsuranceServicesLoad({ store_id: order.store_id, category: 'vision_insurance' }))
    }
  }, [order?.store_id, services?.length])

  const setFormView = (formView) => {
    if (formView === 'form') {
      dispatch(visionInsuranceClaimFormEdit({ activeTab: 'first', activeModule: '', formView: 'form' }))
    } else {
      dispatch(visionInsuranceClaimFormEdit({ formView: 'overview' }))
    }
  }

  useEffect(() => {
    if (!visionInsuranceClaimForm?.id || editing.formView || isDirectSaveOrg) return

    setFormView(visionInsuranceClaimForm.claim_data?.charges?.length ? 'overview' : 'form')
  }, [visionInsuranceClaimForm?.id, isDirectSaveOrg])

  const getActiveModuleArr = (res) => {
    return res?.eligibility?.data?.map(item => item.active && item.service_type ? item.service_type : '') || []
  }

  const getActiveTabModule = (res) => {
    if (isDirectSaveOrg && editing.activeTab === 'first') return

    const { activeTab } = editing
    const activeModulesArr = getActiveModuleArr(res)
    const step2Modules = ['Lens', 'Frame', 'Contact Lens', 'Contact Lens Service']
    const hasStep2 = activeModulesArr.some(m => step2Modules.includes(m))

    switch (activeTab) {
      case 'first':
        dispatch(visionInsuranceClaimFormEdit({ activeTab: 2 }))
        break
      case 2:
        if (hasStep2) {
          dispatch(visionInsuranceClaimFormEdit({ activeTab: 3 }))
        } else {
          dispatch(visionInsuranceClaimFormEdit({ activeTab: 'final' }))
        }
        break
      case 3:
        dispatch(visionInsuranceClaimFormEdit({ activeTab: 'final' }))
        break
      case 'final':
        dispatch(visionInsuranceClaimFormEdit({ activeTab: '', activeModule: '' }))
        dispatch(visionInsuranceClaimFormEdit({ formView: 'overview' }))
        break
      default:
        break
    }
  }

  const validateFormData = (data) => {
    const isAnyModuleSelected = data.eligibility?.data.find(item => item.active)
    let chargesValidateArr = data.claim_data?.charges?.map(item => Number(item.charge))
    const hasNaN = chargesValidateArr ? (chargesValidateArr.find(item => Number.isNaN(item)) !== undefined) : false
    if (!isAnyModuleSelected) {
      Alert.warning('Please select a service to proceed.')
      return false
    }
    if (hasNaN) {
      Alert.warning('Invalid Amount')
      return false
    }
    return true
  }

  const getFilters = (data) => {
    const { activeTab } = editing

    switch (activeTab) {
      case 'first':
        return { claim_sections: ['eligibility'] }
      case 'final':
        return { claim_sections: ['charges'] }
      // case 2:
      //   return { claim_sections: ['insured', 'exams'] }
      // case 3:
      //   return { claim_sections: ['frame', 'lens', 'lab'] }
      default:
        return {}
    }
  }

  const handleClaimFormUpdate = async (data) => {
    const isFormValid = validateFormData(data)
    filters = getFilters(data)
    if (isFormValid) {
      const payload = isDirectSaveOrg ? { ...data, location: selectedLocation } : data
      let res = await dispatch(visionInsuranceClaimFormUpdate(
        payload,
        withOrdersInsuranceId(filters, editing.orders_insurance_id),
      ))
      res = res.value.data.insurance_claim_form
      getActiveTabModule(res)
      filters = {}
    }
    return
  }

  const handleSyncSuccessfully = () => {
    setFormView('form')
  }

  const orderInsurance = order.orders_insurances_attributes?.find(
    (item) => item.id === editing?.orders_insurance_id,
  )
  const claimSubmissionAt = orderInsurance?.claim_submission_at || order.claim_submission_at

  return (
    <div className='px-6'>
      {order.insurance_payer_changed && <h5 className='text-danger mb-5 text-center'>The insurance payer name is changed for the selected insurance.</h5>}
      <div className='d-flex justify-content-between align-items-center'>
        <div>
          {visionInsuranceConfig?.visible && <RpaClaimSubmitButton order={order} />}
          {!isDirectSaveOrg && editing.formView !== 'overview' &&
            <button className='btn btn-outline-primary' onClick={() => dispatch(visionInsuranceClaimFormEdit({ formView: 'overview' }))}>Overview</button>
          }
          {claimSubmissionAt && <span className='text-danger ml-6'>Previous Claim Send on <DateFormatView dateTime={claimSubmissionAt} />
          </span>}
        </div>
        <div className='d-flex align-items-center'>
          {isDirectSaveOrg ? (
            <div className="form-group mb-0 ml-md-5">
              <label className="mb-1" htmlFor="direct-save-claim-location">Location:</label>
              <select
                id="direct-save-claim-location"
                className="form-control"
                style={{ minWidth: '180px' }}
                value={selectedLocation}
                onChange={(e) => setSelectedLocation(e.target.value)}
              >
                {DIRECT_SAVE_CLAIM_OPTIONS.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          ) : (
            <ClaimFormSync label="Reset" handleAfterSyncSuccessfully={handleSyncSuccessfully} />
          )}
        </div>
      </div>
      {editing.formView == 'overview' ?
        <ClaimFormOverview visionInsuranceClaimForm={visionInsuranceClaimForm} claimFormTemplate={claimFormTemplate} /> :
        <form onSubmit={handleSubmit(handleClaimFormUpdate)}>
          <EyefinityStepForm pprops={{ ...props, orderStoreId: order.store_id, firstStepActionLabel }} />
        </form>
      }
    </div>
  )
}

EyefinityClaimForm = reduxForm({
  form: 'EyefinityClaimForm',
  enableReinitialize: true,
  shouldValidate: () => true,
  validate: validateEyefinityClaimForm,
  onSubmitFail: () => ScrollToTarget()
})(EyefinityClaimForm)

const mapStateToProps = (state) => {
  const { organisation: { organisation }, order: { order }, visionInsuranceClaimForm: { visionInsuranceClaimForm, saving, editing }, visionInsurance } = state

  return {
    visionInsuranceConfig: organisation.add_ons_config?.type?.vision_insurance,
    organisation,
    order,
    visionInsuranceClaimForm,
    editing,
    saving,
    services: visionInsurance.services,
    formValues: state.form.EyefinityClaimForm?.values,
    initialValues: {
      ...visionInsuranceClaimForm,
    }
  }
}

export default connect(mapStateToProps)(EyefinityClaimForm)
